import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'

function UserEditModal({ user, roles, onClose, onSave }) {
  const isCreating = !user
  const [userType, setUserType] = useState('employee') // 'employee' or 'admin'

  // Shared fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedRoleIds, setSelectedRoleIds] = useState([])

  // Admin-only fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // For editing, load existing user detail
  const [loadingDetail, setLoadingDetail] = useState(false)
  useEffect(() => {
    if (user?.user_id) {
      loadUserDetail(user.user_id)
    }
  }, [user])

  async function loadUserDetail(userId) {
    setLoadingDetail(true)
    try {
      const detail = await api.get(`/api/users/${userId}`)
      setName(detail.name || '')
      setDescription(detail.description || '')
      setEmail(detail.email || '')
      setSelectedRoleIds((detail.roles || []).map(r => r.role_id))
    } catch (err) {
      console.error('Failed to load user detail', err)
      setError('Failed to load user details')
    } finally {
      setLoadingDetail(false)
    }
  }

  function toggleRole(roleId) {
    setSelectedRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    )
  }

  async function handleSave() {
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    if (isCreating && userType === 'admin') {
      if (!email.trim()) {
        setError('Email is required for admin users')
        return
      }
      if (password.length < 9) {
        setError('Password must be at least 9 characters')
        return
      }
    }

    if (isCreating && userType === 'employee' && selectedRoleIds.length === 0) {
      setError('Select at least one role')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isCreating) {
        let payload
        if (userType === 'admin') {
          payload = {
            name: name.trim(),
            email: email.trim(),
            password,
            description: description.trim() || null,
          }
        } else {
          payload = {
            name: name.trim(),
            description: description.trim() || null,
            role_ids: selectedRoleIds,
          }
        }

        const result = await api.post('/api/users', payload)
        onSave({ name: name.trim(), pin: result.pin || null })
      } else {
        // Update user PII
        await api.patch(`/api/users/${user.user_id}`, {
          name: name.trim(),
          description: description.trim() || null,
        })

        // Update roles
        await api.put(`/api/users/${user.user_id}/roles`, {
          role_ids: selectedRoleIds,
        })

        onSave()
      }
    } catch (err) {
      setError(err.message || 'Failed to save user')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Separate admin role from regular roles
  const adminRole = roles.find(r => r.is_admin)
  const regularRoles = roles.filter(r => !r.is_admin)

  return (
    <div className="modal-overlay">
      <div className="modal-content user-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isCreating ? 'Create User' : `Edit: ${user.name}`}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body-scroll">
          {loadingDetail ? (
            <div className="loading-center"><div className="loading-spinner" /></div>
          ) : (
            <>
              {error && (
                <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>
              )}

              {/* User type toggle (create only) */}
              {isCreating && (
                <div className="form-group">
                  <label className="form-label">User Type</label>
                  <div className="user-type-toggle">
                    <button
                      type="button"
                      className={`toggle-btn ${userType === 'employee' ? 'active' : ''}`}
                      onClick={() => setUserType('employee')}
                    >
                      Employee
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${userType === 'admin' ? 'active' : ''}`}
                      onClick={() => setUserType('admin')}
                    >
                      Admin
                    </button>
                  </div>
                  <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-1)' }}>
                    {userType === 'employee'
                      ? 'Employees log in with Employee ID + PIN. A PIN will be auto-generated.'
                      : 'Admins log in with email + password and have full system access.'}
                  </p>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter name"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </div>

              {/* Email display (editing admin) */}
              {!isCreating && user?.is_admin && email && (
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    readOnly
                    style={{ opacity: 0.7, cursor: 'default' }}
                  />
                </div>
              )}

              {/* Admin-only fields (create only) */}
              {isCreating && userType === 'admin' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Enter email"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input
                      type="password"
                      className="form-input"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Minimum 9 characters"
                    />
                  </div>
                </>
              )}

              {/* Role assignment (employee creation + editing non-admin) */}
              {(isCreating ? userType === 'employee' : !user?.is_admin) && (
                <div className="form-group">
                  <label className="form-label">Roles *</label>
                  <div className="roles-grid">
                    {regularRoles.map(role => (
                      <label key={role.role_id} className={`role-option ${selectedRoleIds.includes(role.role_id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.role_id)}
                          onChange={() => toggleRole(role.role_id)}
                          style={{ display: 'none' }}
                        />
                        <div className="role-name">{role.name}</div>
                        {role.description && (
                          <div className="role-desc">{role.description}</div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loadingDetail}>
            {saving ? 'Saving...' : (isCreating ? 'Create User' : 'Save Changes')}
          </button>
        </div>
      </div>

      <style>{`
        .user-edit-modal {
          width: 100%;
          max-width: 600px;
        }

        .user-type-toggle {
          display: flex;
          gap: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .toggle-btn {
          flex: 1;
          padding: var(--space-2) var(--space-3);
          border: none;
          background: var(--color-bg-secondary);
          cursor: pointer;
          font-size: var(--font-size-sm);
          font-weight: 500;
          color: var(--color-text-muted);
          transition: background 0.15s, color 0.15s;
        }

        .toggle-btn.active {
          background: var(--color-primary);
          color: white;
        }

        .toggle-btn:not(.active):hover {
          background: var(--color-bg-tertiary);
        }

        .roles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: var(--space-2);
        }

        .role-option {
          display: block;
          padding: var(--space-3);
          border: 2px solid var(--color-border);
          border-radius: var(--radius);
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
        }

        .role-option:hover {
          border-color: var(--color-primary);
        }

        .role-option.selected {
          border-color: var(--color-primary);
          background: rgba(59, 130, 246, 0.05);
        }

        .role-name {
          font-weight: 500;
          font-size: var(--font-size-sm);
          margin-bottom: 2px;
        }

        .role-desc {
          font-size: var(--font-size-xs);
          color: var(--color-text-muted);
        }
      `}</style>
    </div>
  )
}

export default UserEditModal
